import type { ArcadeScreen } from "../ScreenManager.js";
import type { MenuAction } from "../MenuInput.js";
import { Reparenter } from "../reparent.js";
import { NavList, type NavItem } from "../navList.js";
import { uiSfx } from "../uiSfx.js";
import { announcer } from "../announcer.js";
import {
  OPTIONS_SECTIONS,
  resolveOptionsSection,
  sectionIndex,
  stepVolume,
  type OptionsSectionId,
} from "./optionsState.js";

/**
 * OPTIONS — CONTROLS / VIDEO / AUDIO / ACCOUNT in one arcade screen
 * (docs/menu-redesign-plan.md §3.9).
 *
 * Existing behavior is ADOPTED, not rebuilt:
 *  - CONTROLS: classic control-method row, BindingsPanel and the live gamepad
 *    test panel move in wholesale; VehicleSelect keeps driving them via
 *    `controlsEnter()/controlsLeave()`.
 *  - VIDEO: the classic SOLID/WIRE/TOON selector moves in; the CRT toggle is
 *    new chrome over M1's crt.ts setter; reduced-motion status is displayed.
 *  - AUDIO: game SFX volume/mute (AudioSystem-backed) + UI blip volume/mute
 *    (uiSfx singleton) — both persist via their own storage keys.
 *  - ACCOUNT: the classic login/register/logout/stats blocks move in with all
 *    their main.ts-wired listeners intact.
 *
 * Nav: ↑/↓ on the section rail switches sections; Enter/► steps into the
 * pane; inside, ↑/↓ moves, ◄/► adjusts sliders, Esc/Ⓑ returns to the rail.
 */

export interface OptionsSelectFacade {
  controlsEnter(): void;
  controlsLeave(): void;
}

export interface GameAudioFacade {
  getVolume(): number;
  setVolume(v: number): void;
  getMuted(): boolean;
  setMuted(m: boolean): void;
}

export interface CrtFacade {
  get(): boolean;
  set(on: boolean): void;
}

export interface OptionsDeps {
  select: OptionsSelectFacade;
  gameAudio: GameAudioFacade;
  crt: CrtFacade;
  reducedMotion: () => boolean;
  onBack: () => void;
}

/** Classic element id → slot selector while adopted here. */
const ADOPTIONS: ReadonlyArray<readonly [id: string, slot: string]> = [
  ["control-method-row", ".op-slot-ctrlmethod"],
  ["bindings-inline", ".op-slot-bindings"],
  ["gamepad-test-panel", ".op-slot-gptest"],
  ["visuals-group", ".op-slot-visuals"],
  ["acct-error", ".op-slot-account"],
  ["acct-guest", ".op-slot-account"],
  ["acct-logged", ".op-slot-account"],
];

const ACCOUNT_INPUT_IDS = [
  "acct-login-user",
  "acct-login-pass",
  "acct-reg-user",
  "acct-reg-pass",
  "acct-reg-pass2",
] as const;

export class OptionsScreen implements ArcadeScreen {
  private root: HTMLElement | null = null;
  private readonly adopter = new Reparenter();
  private readonly railNav = new NavList(() => uiSfx.play("nav"));
  private readonly paneNav = new NavList(() => uiSfx.play("nav"));
  private zone: "rail" | "pane" = "rail";
  private section: OptionsSectionId = "controls";
  private railBtns: HTMLButtonElement[] = [];
  private paneEls = new Map<OptionsSectionId, HTMLElement>();
  private escListenersAttached = false;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  // audio controls
  private gameToggle: HTMLButtonElement | null = null;
  private gameSlider: HTMLInputElement | null = null;
  private uiToggle: HTMLButtonElement | null = null;
  private uiSlider: HTMLInputElement | null = null;
  private annToggle: HTMLButtonElement | null = null;
  private annSlider: HTMLInputElement | null = null;
  private annVoiceBtn: HTMLButtonElement | null = null;
  private annVoiceName: HTMLElement | null = null;
  private crtBtns: HTMLButtonElement[] = [];

  constructor(private readonly deps: OptionsDeps) {}

  mount(root: HTMLElement): void {
    this.root = root;
    root.innerHTML = `
      <div class="op-wrap">
        <div class="op-head">
          <span class="op-eyebrow">OPTIONS</span>
          <span class="op-headrule" aria-hidden="true"></span>
        </div>
        <div class="op-cols">
          <nav class="op-rail" aria-label="Options sections"></nav>
          <div class="op-pane">
            <section class="op-section" data-sec="controls">
              <div class="op-slot op-slot-ctrlmethod"></div>
              <div class="op-slot op-slot-bindings"></div>
              <div class="op-slot op-slot-gptest"></div>
            </section>
            <section class="op-section hidden" data-sec="video">
              <div class="op-slot op-slot-visuals"></div>
              <div class="op-row">
                <span class="op-row-label">CRT EFFECTS</span>
                <div class="op-seg">
                  <button type="button" class="op-seg-btn" data-crt="on">ON</button>
                  <button type="button" class="op-seg-btn" data-crt="off">OFF</button>
                </div>
              </div>
              <p class="op-note op-note-motion hidden">
                SYSTEM PREFERS REDUCED MOTION — CRT and flicker effects default off.
              </p>
              <p class="op-note">Render mode applies from the next match. CRT applies instantly in menus.</p>
            </section>
            <section class="op-section hidden" data-sec="audio">
              <div class="op-row">
                <span class="op-row-label">GAME SFX</span>
                <button type="button" class="op-toggle op-game-toggle"></button>
                <input type="range" class="op-slider op-game-slider" min="0" max="100" step="5"
                       aria-label="Game SFX volume" />
              </div>
              <div class="op-row">
                <span class="op-row-label">UI BLIPS</span>
                <button type="button" class="op-toggle op-ui-toggle"></button>
                <input type="range" class="op-slider op-ui-slider" min="0" max="100" step="5"
                       aria-label="UI blip volume" />
              </div>
              <div class="op-row">
                <span class="op-row-label">ANNOUNCER</span>
                <button type="button" class="op-toggle op-ann-toggle"></button>
                <input type="range" class="op-slider op-ann-slider" min="0" max="100" step="5"
                       aria-label="Announcer volume" />
              </div>
              <div class="op-row">
                <span class="op-row-label">VOICE</span>
                <button type="button" class="op-toggle op-ann-voice" data-cycle="voice"
                        aria-label="Announcer voice">◂ <span class="op-ann-voice-name">ADAM</span> ▸</button>
              </div>
              <p class="op-note">Game SFX settings are saved now and apply from the next session.</p>
            </section>
            <section class="op-section hidden" data-sec="account">
              <div class="op-slot op-slot-account"></div>
            </section>
          </div>
        </div>
        <div class="op-footer">
          <span class="op-hints">↑↓ SELECT &nbsp;·&nbsp; ENTER / Ⓐ CONFIRM &nbsp;·&nbsp; ◄ ► ADJUST &nbsp;·&nbsp; ESC / Ⓑ BACK</span>
        </div>
      </div>`;

    // Section rail
    const rail = root.querySelector(".op-rail")!;
    this.railBtns = [];
    OPTIONS_SECTIONS.forEach((sec) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "op-rail-entry";
      btn.dataset["sec"] = sec.id;
      btn.innerHTML = `<span class="op-rail-label">${sec.label}</span><span class="op-rail-sub">${sec.sub}</span>`;
      btn.addEventListener("mouseenter", () => {
        this.toRail();
        this.railNav.focusTo(sectionIndex(sec.id), true);
        this.showSection(sec.id);
      });
      btn.addEventListener("click", () => {
        this.showSection(sec.id);
        this.enterPane();
      });
      rail.appendChild(btn);
      this.railBtns.push(btn);
    });
    this.railNav.setItems(
      this.railBtns.map((el, i) => ({
        el,
        activate: () => {
          this.showSection(OPTIONS_SECTIONS[i]!.id);
          this.enterPane();
        },
      })),
    );

    for (const secEl of root.querySelectorAll<HTMLElement>(".op-section")) {
      this.paneEls.set(secEl.dataset["sec"] as OptionsSectionId, secEl);
    }

    // Pane clicks can change what is visible (login/register swap, buys…) —
    // re-collect focusables after the handlers ran.
    root.querySelector(".op-pane")?.addEventListener("click", () => {
      queueMicrotask(() => this.refreshPaneItems(true));
      if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refreshPaneItems(true), 400);
    });

    // VIDEO — CRT segmented toggle (M1 crt.ts setter).
    this.crtBtns = [...root.querySelectorAll<HTMLButtonElement>(".op-seg-btn[data-crt]")];
    this.crtBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        this.deps.crt.set(btn.dataset["crt"] === "on");
        this.syncVideoControls();
      });
    });

    // AUDIO — game SFX + UI blips.
    this.gameToggle = root.querySelector(".op-game-toggle");
    this.gameSlider = root.querySelector(".op-game-slider");
    this.uiToggle = root.querySelector(".op-ui-toggle");
    this.uiSlider = root.querySelector(".op-ui-slider");

    this.gameToggle?.addEventListener("click", () => {
      this.deps.gameAudio.setMuted(!this.deps.gameAudio.getMuted());
      this.syncAudioControls();
    });
    this.gameSlider?.addEventListener("input", () => {
      this.deps.gameAudio.setVolume(Number(this.gameSlider!.value) / 100);
    });
    this.uiToggle?.addEventListener("click", () => {
      const on = uiSfx.toggle();
      if (on) uiSfx.play("confirm");
      this.syncAudioControls();
    });
    this.uiSlider?.addEventListener("input", () => {
      uiSfx.setVolume(Number(this.uiSlider!.value) / 100);
    });
    this.uiSlider?.addEventListener("change", () => uiSfx.play("nav"));

    // ANNOUNCER — toggle / volume / voice pack (announcer singleton, uiSfx-style).
    this.annToggle = root.querySelector(".op-ann-toggle");
    this.annSlider = root.querySelector(".op-ann-slider");
    this.annVoiceBtn = root.querySelector(".op-ann-voice");
    this.annVoiceName = root.querySelector(".op-ann-voice-name");

    this.annToggle?.addEventListener("click", () => {
      announcer.setEnabled(!announcer.enabled);
      if (announcer.enabled) announcer.preview();
      this.syncAudioControls();
    });
    this.annSlider?.addEventListener("input", () => {
      announcer.setVolume(Number(this.annSlider!.value) / 100);
    });
    this.annVoiceBtn?.addEventListener("click", () => this.cycleVoice(1));
    // Pack labels come from the manifest when it exists; the fallback list
    // keeps the picker operable before the assets land.
    void announcer.loadManifest().then(() => this.syncAudioControls());
  }

  /** Step the announcer voice pack and A/B it with a short preview line. */
  private cycleVoice(delta: 1 | -1): void {
    const packs = announcer.packs();
    if (packs.length === 0) return;
    const idx = packs.findIndex((p) => p.id === announcer.pack);
    const next = packs[(((idx === -1 ? 0 : idx) + delta) % packs.length + packs.length) % packs.length]!;
    announcer.setPack(next.id);
    announcer.preview();
    this.syncAudioControls();
  }

  onEnter(params?: unknown): void {
    this.adoptAll();
    this.attachEscBlur();
    this.syncVideoControls();
    this.syncAudioControls();
    if (this.root) {
      this.root
        .querySelector(".op-note-motion")
        ?.classList.toggle("hidden", !this.deps.reducedMotion());
    }
    this.toRail();
    this.showSection(resolveOptionsSection(params));
    this.railNav.focusTo(sectionIndex(this.section), false);
  }

  onLeave(): void {
    if (this.section === "controls") this.deps.select.controlsLeave();
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.paneNav.clearFocus();
    this.adopter.releaseAll();
  }

  handleInput(action: MenuAction): boolean {
    if (this.zone === "rail") {
      switch (action.type) {
        case "up":
          this.railNav.move(-1);
          this.showSection(OPTIONS_SECTIONS[this.railNav.focusIdx()]!.id);
          return true;
        case "down":
          this.railNav.move(1);
          this.showSection(OPTIONS_SECTIONS[this.railNav.focusIdx()]!.id);
          return true;
        case "confirm":
        case "start":
        case "right":
          this.enterPane();
          return true;
        case "left":
          return true;
        case "back":
          uiSfx.play("back");
          this.deps.onBack();
          return true;
        default:
          return false;
      }
    }
    // pane zone
    switch (action.type) {
      case "up":
        this.paneNav.move(-1);
        return true;
      case "down":
        this.paneNav.move(1);
        return true;
      case "left":
        this.paneNav.adjust(-1);
        return true;
      case "right":
        this.paneNav.adjust(1);
        return true;
      case "confirm":
      case "start":
        if (this.paneNav.activate()) uiSfx.play("confirm");
        return true;
      case "back":
        uiSfx.play("back");
        this.toRail();
        this.railNav.applyFocus();
        return true;
      default:
        return false;
    }
  }

  // ── internals ───────────────────────────────────────────────────────────

  private adoptAll(): void {
    const root = this.root;
    if (!root) return;
    for (const [id, slotSel] of ADOPTIONS) {
      const el = document.getElementById(id);
      const slot = root.querySelector<HTMLElement>(slotSel);
      if (el && slot) this.adopter.claim(el, slot);
    }
  }

  /** Esc inside an adopted account input blurs it so Esc/Ⓑ can navigate again. */
  private attachEscBlur(): void {
    if (this.escListenersAttached) return;
    this.escListenersAttached = true;
    for (const id of ACCOUNT_INPUT_IDS) {
      const el = document.getElementById(id);
      el?.addEventListener("keydown", (ev) => {
        if ((ev as KeyboardEvent).code === "Escape") (el as HTMLInputElement).blur();
      });
    }
  }

  private showSection(id: OptionsSectionId): void {
    if (this.section === "controls" && id !== "controls") this.deps.select.controlsLeave();
    const entering = this.section !== id;
    this.section = id;
    for (const [sec, el] of this.paneEls) el.classList.toggle("hidden", sec !== id);
    this.railBtns.forEach((btn) => btn.classList.toggle("is-selected", btn.dataset["sec"] === id));
    if (id === "controls") this.deps.select.controlsEnter();
    if (entering || this.paneNav.length() === 0) this.refreshPaneItems(false);
  }

  private toRail(): void {
    this.zone = "rail";
    this.paneNav.clearFocus();
  }

  private enterPane(): void {
    this.refreshPaneItems(true);
    if (this.paneNav.length() === 0) {
      uiSfx.play("deny");
      return;
    }
    this.zone = "pane";
    this.railNav.clearFocus();
    this.railBtns.forEach((btn) =>
      btn.classList.toggle("is-selected", btn.dataset["sec"] === this.section),
    );
    this.paneNav.applyFocus();
    uiSfx.play("confirm");
  }

  /** Collect the visible interactive controls of the current section. */
  private refreshPaneItems(preserveIndex: boolean): void {
    const pane = this.paneEls.get(this.section);
    if (!pane) return;
    const items: NavItem[] = [];
    const controls = pane.querySelectorAll<HTMLElement>("button, input");
    controls.forEach((el) => {
      if (el.classList.contains("hidden")) return;
      if (!isDisplayed(el)) return;
      if (el instanceof HTMLInputElement && el.type === "range") {
        items.push({
          el,
          activate: () => el.focus(),
          adjust: (delta) => {
            const facade = el === this.gameSlider ? this.deps.gameAudio : null;
            const cur = Number(el.value) / 100;
            const next = stepVolume(cur, delta);
            el.value = String(Math.round(next * 100));
            if (facade) facade.setVolume(next);
            else if (el === this.uiSlider) {
              uiSfx.setVolume(next);
              uiSfx.play("nav");
            } else {
              el.dispatchEvent(new Event("input", { bubbles: true }));
            }
            return true;
          },
        });
        return;
      }
      if (el instanceof HTMLInputElement) {
        items.push({ el, activate: () => el.focus() });
        return;
      }
      if (el.dataset["cycle"] !== undefined) {
        // ◂ VALUE ▸ cycle rows (announcer voice): confirm steps forward,
        // pad/keyboard left/right step both ways.
        items.push({
          el,
          activate: () => (el as HTMLButtonElement).click(),
          adjust: (delta) => {
            if (el === this.annVoiceBtn) this.cycleVoice(delta);
            else (el as HTMLButtonElement).click();
            return true;
          },
        });
        return;
      }
      items.push({ el, activate: () => (el as HTMLButtonElement).click() });
    });
    this.paneNav.setItems(items, preserveIndex);
    if (this.zone === "pane") this.paneNav.applyFocus();
    else this.paneNav.clearFocus();
  }

  private syncVideoControls(): void {
    const on = this.deps.crt.get();
    this.crtBtns.forEach((btn) =>
      btn.classList.toggle("selected", (btn.dataset["crt"] === "on") === on),
    );
  }

  private syncAudioControls(): void {
    if (this.gameToggle) {
      const muted = this.deps.gameAudio.getMuted();
      this.gameToggle.textContent = muted ? "OFF" : "ON";
      this.gameToggle.classList.toggle("is-off", muted);
    }
    if (this.gameSlider) this.gameSlider.value = String(Math.round(this.deps.gameAudio.getVolume() * 100));
    if (this.uiToggle) {
      this.uiToggle.textContent = uiSfx.enabled ? "ON" : "OFF";
      this.uiToggle.classList.toggle("is-off", !uiSfx.enabled);
    }
    if (this.uiSlider) this.uiSlider.value = String(Math.round(uiSfx.volume * 100));
    if (this.annToggle) {
      this.annToggle.textContent = announcer.enabled ? "ON" : "OFF";
      this.annToggle.classList.toggle("is-off", !announcer.enabled);
    }
    if (this.annSlider) this.annSlider.value = String(Math.round(announcer.volume * 100));
    if (this.annVoiceName) {
      const pack = announcer.packs().find((p) => p.id === announcer.pack);
      this.annVoiceName.textContent = pack?.label ?? announcer.pack.toUpperCase();
    }
  }
}

/** Visible enough to focus (display:none ancestors excluded). */
function isDisplayed(el: HTMLElement): boolean {
  return el.offsetParent !== null || el.offsetWidth > 0 || el.offsetHeight > 0;
}
